import * as THREE from 'three';
import type { InstancedMeshIntersection, InstanceProps, PickResult } from '../types';
import { IdAllocator } from './IdAllocator';
import { disposeMaterials } from './Util';
import { type MutableRefObject } from 'react';
import { type ThreeEvent } from '@react-three/fiber';

/**
 * MeshPickHelper implements fast picking using GPU rendering facility.
 *
 * Unique 24 bit ids assigned to all pickable instances.
 * To pick an instance we render the scene using picking id colors (24 bit, rgb). We render only pixels around mouse pointer (targetSize).
 * Colors from rendered scene converted to 24 bit picking ids and mapped back to particular instance.
 */
export class MeshPickHelper {
  private static readonly minTargetSize = 5;

  private renderTarget: THREE.WebGLRenderTarget | null = null;

  private pixelBuffer: Uint8Array | null = null;

  private pickingScene = new THREE.Scene();

  private instancedMeshes = new Map<
    string,
    {
      instancedMesh: THREE.InstancedMesh;
      pickingMesh: THREE.InstancedMesh;
      allocatedIdRanges: number[];
      hoveredInstance: MutableRefObject<{ index: number; instance: InstanceProps } | undefined>;
    }
  >();

  private idRanges = new Map<
    number,
    {
      meshKey: string;
      groupKey: string;
      pickingMesh: THREE.InstancedMesh;
      hoveredInstance: MutableRefObject<{ index: number; instance: InstanceProps } | undefined>;
      offset: number;
      onClick: (instanceIndex: number, event: ThreeEvent<PointerEvent>) => void;
    }
  >();

  // Assigning unique picking ids for all instances of InstancedMeshes (from 24 bit global range).
  // Global range splited in 4K chunks (ranges).
  // Unique picking id (24 bit) consists of two parts: [rangeId, rangePointIndex].
  // One or more ranges get assigned to PointCloudNode points.
  // idRanges contains mapping if rangeId to geometry buffer and offset.
  // Point index in geometry buffer is idRanges.offset + rangePointIndex.
  private static readonly rangeBits = 12;

  private static readonly rangeSize = 1 << MeshPickHelper.rangeBits;

  // excluding 0, to ensure all ids are non-zero, zero used as clear color
  private static readonly minRangeId = 1;

  private static readonly maxRangeId = (1 << (24 - MeshPickHelper.rangeBits)) - 1;

  private rangeIdAllocator = new IdAllocator(MeshPickHelper.minRangeId, MeshPickHelper.maxRangeId);

  addInstancedMesh(
    meshKey: string,
    groupKey: string,
    instancedMesh: THREE.InstancedMesh,
    // hoveredMesh: THREE.Mesh,
    hoveredInstance: MutableRefObject<{ index: number; instance: InstanceProps } | undefined>,
    onClick: (instanceIndex: number, event: ThreeEvent<PointerEvent>) => void
  ) {
    const { instancedMeshes, pickingScene, rangeIdAllocator, idRanges } = this;

    if (instancedMeshes.get(meshKey)) {
      throw new Error('MeshPickHelper: key already exists');
    }

    const numberInstances = instancedMesh.instanceMatrix.count;

    const srcMaterial: THREE.Material = instancedMesh.material as THREE.Material;

    const material = new THREE.MeshBasicMaterial({
      blending: THREE.NoBlending,
      side: THREE.DoubleSide,
      toneMapped: true,
      clippingPlanes: srcMaterial.clippingPlanes ?? undefined,
    });
    const pickingMesh = new THREE.InstancedMesh(instancedMesh.geometry, material, numberInstances);
    pickingMesh.instanceMatrix = instancedMesh.instanceMatrix;
    pickingMesh.renderOrder = instancedMesh.renderOrder;
    pickingMesh.layers = instancedMesh.layers;

    // adding picking ids
    const allocatedIdRanges = [];
    const color = new THREE.Color();

    for (let offset = 0; offset < numberInstances; offset += MeshPickHelper.rangeSize) {
      const idRange = rangeIdAllocator.allocate();
      if (idRange === undefined) {
        throw new Error('rangeIdAllocator failed');
      }
      allocatedIdRanges.push(idRange);
      idRanges.set(idRange, {
        meshKey,
        groupKey,
        pickingMesh,
        hoveredInstance,
        offset,
        onClick,
      });
      const rangeEnd = Math.min(offset + MeshPickHelper.rangeSize, numberInstances);
      for (let index = offset; index < rangeEnd; index += 1) {
        const rangePointIndex = index - offset;
        const pickId = idRange * MeshPickHelper.rangeSize + rangePointIndex;
        const r = (pickId & 0xff) / 255;
        const g = ((pickId >> 8) & 0xff) / 255;
        const b = ((pickId >> 16) & 0xff) / 255;
        color.set(r, g, b);
        pickingMesh.setColorAt(index, color);
      }
      // pickingMesh.instanceMatrix.needsUpdate = true;
      pickingMesh.instanceColor!.needsUpdate = true;
    }
    pickingScene.add(pickingMesh);
    instancedMeshes.set(meshKey, {
      instancedMesh,
      pickingMesh,
      allocatedIdRanges,
      hoveredInstance,
    });
    // console.log(`MeshPickHelper.addInstancedMesh ${key}   ${instancedMeshes.size}`);
  }

  removeInstancedMesh(meshKey: string) {
    const { instancedMeshes, pickingScene, idRanges, rangeIdAllocator } = this;
    const entry = instancedMeshes.get(meshKey);
    if (entry !== undefined) {
      const { pickingMesh, allocatedIdRanges } = entry;
      allocatedIdRanges.forEach((idRange) => {
        idRanges.delete(idRange);
        rangeIdAllocator.release(idRange);
      });
      pickingScene.remove(pickingMesh);
      pickingMesh.geometry.dispose();
      disposeMaterials(pickingMesh.material);
      pickingMesh.dispose();
      instancedMeshes.delete(meshKey);
      // console.log(`MeshPickHelper.removeInstancedMesh ${key}  ${instancedMeshes.size}`);
    }
  }

  setTargetSize(targetSize: number): void {
    if (this.renderTarget === null || this.renderTarget.height !== targetSize) {
      this.renderTarget = new THREE.WebGLRenderTarget(targetSize, targetSize);
      this.pixelBuffer = new Uint8Array(4 * targetSize * targetSize);
    }
  }

  scanPixelBufferFromCenter(halfTargetSize: number, targetSize: number): number {
    const middle = halfTargetSize;
    for (let boxHalfSize = 0; boxHalfSize <= halfTargetSize; boxHalfSize += 1) {
      // scanning perimeter of the box (of 2 * boxHalfSize + 1 size)
      for (const coord0 of [middle - boxHalfSize, middle + boxHalfSize]) {
        for (let coord1 = middle - boxHalfSize; coord1 <= middle + boxHalfSize; coord1 += 1) {
          for (const pixelIndex of [coord0 * targetSize + coord1, coord1 * targetSize + coord0]) {
            const id = this.getPixelAsId(this.pixelBuffer!, pixelIndex);
            if (id !== 0) {
              return id;
            }
          }
        }
      }
    }
    return 0;
  }

  pickObject(
    centerX: number,
    centerY: number,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    canvasAreaPercent: number | undefined
  ): InstancedMeshIntersection | undefined {
    const { pickingScene, idRanges, renderPickingArea, instancedMeshes } = this;

    this.instancedMeshes.forEach(({ instancedMesh, pickingMesh, hoveredInstance }) => {
      // array has one more entry for hoveredInstance
      pickingMesh.count = instancedMesh.count + (hoveredInstance.current ? 1 : 0);
    });

    const canvas = renderer.domElement;
    const halfTargetSize = canvasAreaPercent
      ? Math.max(
          Math.trunc(MeshPickHelper.minTargetSize / 2),
          Math.round((canvas.height * canvasAreaPercent) / 200)
        )
      : 1;
    // assuming odd targetSize in scanPixelBufferFromCenter
    const targetSize = 2 * halfTargetSize + 1;
    this.setTargetSize(targetSize);
    const { renderTarget, pixelBuffer } = this;

    const pixelRatio = renderer.getPixelRatio();
    const x = centerX / pixelRatio;
    const y = centerY / pixelRatio;

    const { drawingBufferWidth, drawingBufferHeight } = renderer.getContext();

    renderPickingArea(
      camera,
      renderer,
      renderTarget!,
      pixelBuffer!,
      pickingScene,
      drawingBufferHeight,
      drawingBufferWidth,
      x,
      y,
      pixelRatio
    );

    const id = this.scanPixelBufferFromCenter(halfTargetSize, targetSize);

    if (id === 0) {
      return undefined;
    }

    const rangeId = id >> MeshPickHelper.rangeBits;
    // console.log('id', id, 'rangeId', rangeId);
    const rangeData = idRanges.get(rangeId);
    if (rangeData !== undefined) {
      const { meshKey, groupKey, offset, onClick, pickingMesh, hoveredInstance } = rangeData;
      const rangePointIndex = id - rangeId * MeshPickHelper.rangeSize;
      let instanceIndex = offset + rangePointIndex;
      if (instanceIndex === pickingMesh.count - 1 && hoveredInstance.current) {
        // last entry in picking is copy of hovered entry
        instanceIndex = hoveredInstance.current.index;
      }

      const entry = instancedMeshes.get(meshKey);

      if (!entry) {
        return undefined;
      }
      // to obtain intersection point, we could get distance from depth buffer
      // doing simpler way - using translation of instance as intersection point

      // local transformation of instance
      const matrix4 = new THREE.Matrix4();
      pickingMesh.getMatrixAt(instanceIndex, matrix4);
      const point = new THREE.Vector3(0, 0, 0);
      point.applyMatrix4(matrix4);
      point.add(pickingMesh.position);

      const pickInfo: PickResult = {
        meshKey,
        groupKey,
        instanceIndex,
        onClick,
      };

      return {
        distance: camera.position.distanceTo(point),
        point,
        object: entry.instancedMesh,
        instanceId: instanceIndex,
        pickInfo,
      };
    }

    return undefined;
  }

  pick(
    event: PointerEvent | MouseEvent,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    renderer: THREE.WebGLRenderer,
    canvasAreaPercent: number | undefined
  ): PickResult | undefined {
    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = event;
    const centerX = ((clientX - rect.left) * canvas.width) / rect.width;
    const centerY = ((clientY - rect.top) * canvas.height) / rect.height;

    const intersection = this.pickObject(centerX, centerY, camera, renderer, canvasAreaPercent);

    if (intersection) {
      const { pickInfo } = intersection;
      return { ...pickInfo };
    }

    return undefined;
  }

  public dispose() {
    this.instancedMeshes.forEach((_node, key) => {
      this.removeInstancedMesh(key);
    });
    this.renderTarget?.dispose();
  }

  private getPixelAsId = (pixelBuffer: Uint8Array, index: number) => {
    return (
      (pixelBuffer[4 * index + 2] << 16) |
      (pixelBuffer[4 * index + 1] << 8) |
      pixelBuffer[4 * index + 0]
    );
  };

  // Renders a subsection of the camera with the specified center, width, height (picking area).
  // The pixels of picking area are decoded to get point data for any point within the picking area.
  private renderPickingArea = (
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    renderTarget: THREE.WebGLRenderTarget,
    pixelBuffer: Uint8Array,
    pickingScene: THREE.Scene,
    drawingBufferHeight: number,
    drawingBufferWidth: number,
    x: number,
    y: number,
    pixelRatio: number
  ) => {
    if (
      !(camera instanceof THREE.PerspectiveCamera || camera instanceof THREE.OrthographicCamera)
    ) {
      return;
    }
    const areaWidth = renderTarget.width;
    const areaHeight = renderTarget.height;
    camera.setViewOffset(
      drawingBufferWidth, // full width
      drawingBufferHeight, // full top
      x * pixelRatio - areaWidth / 2, // center x
      y * pixelRatio - areaHeight / 2, // center y
      areaWidth, // rect width
      areaHeight // rect height
    );

    // render the scene
    renderer.autoClear = true;
    renderer.setRenderTarget(renderTarget);
    renderer.render(pickingScene, camera);
    renderer.setRenderTarget(null);

    // clear the view offset so rendering returns to normal
    camera.clearViewOffset();

    renderer.readRenderTargetPixels(
      renderTarget,
      0, // x
      0, // y
      areaWidth, // width
      areaHeight, // height
      pixelBuffer
    );
  };
}

export default MeshPickHelper;
